import { z } from 'zod';
import { Email, IsoDateTime, LocalDate, PageQuery, Phone, Uuid, page } from './common.js';
/** Module `lone-working` (docs/modules/lone-working.md, ADR 0023, spec 8.6.5). Owner: task S33. */
export const LoneWorkState = z.enum(['ok', 'prompted', 'second_prompt', 'escalated', 'panic', 'ended']);
export const LoneWorkContact = z.object({
    id: Uuid,
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    order: z.number().int(),
});
export const LoneWorkContactsPut = z.object({
    contacts: z
        .array(z.object({ name: z.string().trim().min(1).max(120), phone: Phone.nullable().default(null), email: Email.nullable().default(null) }).refine((c) => !!c.phone || !!c.email, { message: 'phone or email' }))
        .max(5),
});
export const LoneWorkSession = z.object({
    id: Uuid,
    memberId: Uuid,
    memberName: z.string(),
    siteId: Uuid,
    siteName: z.string(),
    visitId: Uuid.nullable(),
    state: LoneWorkState,
    startedAt: IsoDateTime,
    plannedEndAt: IsoDateTime.nullable(),
    endedAt: IsoDateTime.nullable(),
    intervalMinutes: z.number().int(),
    lastCheckInAt: IsoDateTime.nullable(),
    nextDueAt: IsoDateTime.nullable(),
    /** No contact can be reached (shown in red). */
    noContacts: z.boolean(),
    openEscalationId: Uuid.nullable(),
});
export const LoneWorkEscalation = z.object({
    id: Uuid,
    sessionId: Uuid,
    memberName: z.string(),
    siteName: z.string(),
    reason: z.enum(['missed_check_in', 'panic']),
    startedAt: IsoDateTime,
    repeats: z.number().int(),
    /** Only for panic, only that moment's position. */
    location: z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number() }).nullable(),
    acknowledgedAt: IsoDateTime.nullable(),
    acknowledgedByLabel: z.string().nullable(),
    resolvedAt: IsoDateTime.nullable(),
    resolution: z.string().nullable(),
    /** The person checked in after it started (all clear sent). */
    clearedByCheckInAt: IsoDateTime.nullable(),
    /** Seconds the ticker was late when it escalated (a server restart); 0 when on time. */
    delaySeconds: z.number().int().default(0),
    /** Urgent messages that failed all their attempts (ADR 0023): the board shows a warning. */
    deliveryFailures: z.number().int().default(0),
});
export const EscalationResolve = z.object({ resolution: z.string().trim().min(1).max(1000) });
/** Portal board: GET /lone-working/board */
export const LoneWorkBoard = z.object({
    sessions: z.array(LoneWorkSession),
    escalations: z.array(LoneWorkEscalation),
    /** The ticker heartbeat is stale (ADR 0023 watchdog). */
    monitoringDelayed: z.boolean(),
    /** Why monitoring is delayed: the ticker stopped, or it stopped and nobody has an address to be told at (no watchdog email, no reception email). */
    monitoringDelayedReason: z.enum(['ticker_stopped', 'no_watchdog_email']).nullable().default(null),
});
// ---------- Companion ----------
export const MyLoneWork = z.object({
    applies: z.boolean(),
    canStartManually: z.boolean(),
    session: LoneWorkSession.nullable(),
    contacts: z.array(LoneWorkContact),
    canEditContacts: z.boolean(),
    graceMinutes: z.number().int(),
    /** The group lets a check-in carry the phone position (companion-phase3 section 2). Absent = no. */
    shareLocationOnCheckIn: z.boolean().optional(),
});
export const LoneWorkStart = z.object({ idempotencyKey: Uuid, siteId: Uuid, plannedEndAt: IsoDateTime.nullable().default(null), occurredAt: IsoDateTime });
export const LoneWorkCheckIn = z.object({
    idempotencyKey: Uuid,
    occurredAt: IsoDateTime,
    location: z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number() }).nullable().default(null),
});
export const LoneWorkPanic = z.object({
    idempotencyKey: Uuid,
    occurredAt: IsoDateTime,
    location: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180), accuracyM: z.number().min(0).max(10000) }).nullable(),
});
export const LoneWorkEnd = z.object({ idempotencyKey: Uuid, occurredAt: IsoDateTime });
export const LoneWorkReportQuery = PageQuery.extend({ from: LocalDate, to: LocalDate, siteId: Uuid.optional(), memberId: Uuid.optional() });
export const LoneWorkReportRow = z.object({
    sessionId: Uuid,
    memberName: z.string(),
    siteName: z.string(),
    startedAt: IsoDateTime,
    endedAt: IsoDateTime.nullable(),
    checkIns: z.number().int(),
    missed: z.number().int(),
    escalations: z.number().int(),
    panics: z.number().int(),
    /** Seconds from escalation to acknowledgement, longest in the session. */
    slowestResponseSeconds: z.number().int().nullable(),
});
export const LoneWorkReport = page(LoneWorkReportRow);
// ---------- Settings ----------
export const LoneWorkingGroupSettings = z.object({
    enabled: z.boolean().default(false),
    intervalMinutes: z.number().int().min(10).max(720).default(60),
    graceMinutes: z.number().int().min(2).max(60).default(10),
    secondGraceMinutes: z.number().int().min(2).max(60).default(5),
    /** Another system already prompts these people (spec 8.6.5 "Overlap"). */
    promptsOff: z.boolean().default(false),
    allowManualStart: z.boolean().default(true),
    membersEditContacts: z.boolean().default(false),
    shareLocationOnCheckIn: z.boolean().default(false),
    /** Used when a member has no contacts of their own. */
    fallbackContacts: z
        .array(z.object({ name: z.string().max(120), phone: Phone.nullable().default(null), email: Email.nullable().default(null) }))
        .max(5)
        .default([]),
});
export const LoneWorkingSiteSettings = z.object({
    /** Everyone signed in here is lone working (remote sites are always eligible when the group is enabled). */
    enabled: z.boolean().default(false),
});
export const LoneWorkingTenantSettings = z.object({
    repeatEscalationMinutes: z.number().int().min(0).max(120).default(15),
    maxEscalationRepeats: z.number().int().min(0).max(10).default(4),
    retentionDays: z.number().int().min(0).default(730),
});
export const LoneWorkingPlatformSettings = z.object({
    tickSeconds: z.number().int().min(15).max(120).default(30),
    /** Emailed when the ticker heartbeat is older than 3 minutes. Empty = the reception emails of the sites where someone is being watched. */
    watchdogEmail: z.string().max(254).default(''),
});
//# sourceMappingURL=lone-working.js.map